"use client";

import { useState, type CSSProperties, type ReactNode } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Eye } from "lucide-react";
import { SafeImage } from "@/components/ui/safe-image";
import { CompareButton } from "@/components/product/compare-button";
import { FlyToCart } from "@/components/product/fly-to-cart";
import { QuickAddButton } from "@/components/product/quick-add-button";
import { WishlistButton } from "@/components/product/wishlist-button";
import { useDealerStatus } from "@/hooks/use-dealer-status";
import { discountPercent, formatCurrency, mediaUrl } from "@/lib/utils";
import type { RecentProduct } from "@/store/recently-viewed.store";
import type { Product } from "@/types";

const QuickViewModal = dynamic(
  () => import("@/components/product/quick-view-modal").then((m) => m.QuickViewModal),
  { ssr: false },
);

const BLUR_DATA_URL = "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";
const CHIP_ROW_H = 22;

interface ProductCardProps {
  product: Product | RecentProduct;
  index?: number;
}

function ChipTicker({ chips }: { chips: { key: string; node: ReactNode }[] }) {
  if (chips.length === 0) return null;

  if (chips.length === 1) {
    return (
      <div className="overflow-hidden" style={{ height: CHIP_ROW_H }}>
        <div className="flex items-center" style={{ height: CHIP_ROW_H }}>{chips[0].node}</div>
      </div>
    );
  }

  const list = [...chips, { ...chips[0], key: `${chips[0].key}-clone` }];

  return (
    <div className="overflow-hidden" style={{ height: CHIP_ROW_H }}>
      <div className="animate-chip-ticker" style={{ "--chip-row-h": `${CHIP_ROW_H}px` } as CSSProperties}>
        {list.map((chip) => (
          <div key={chip.key} className="flex items-center" style={{ height: CHIP_ROW_H }}>
            {chip.node}
          </div>
        ))}
      </div>
    </div>
  );
}

export function BotanicalProductCard({ product, index = 99 }: ProductCardProps) {
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [isFlying, setIsFlying] = useState(false);
  const [flyStartRect, setFlyStartRect] = useState<DOMRect | undefined>(undefined);
  const discount = discountPercent(product.price, product.compareAtPrice);
  const isOutOfStock = product.trackStock && product.stock <= 0;
  const threshold = product.lowStockThreshold ?? 5;
  const isLowStock = product.trackStock && product.stock > 0 && product.stock <= threshold;
  const showNewBadge = product.isFeatured && !discount;
  const { isApproved, discountPercentage } = useDealerStatus();
  const dealerPrice = isApproved
    ? (product.dealerPrice ?? (discountPercentage > 0 ? product.price * (1 - discountPercentage / 100) : null))
    : null;
  const displayDiscountPercentage = product.dealerPrice && product.dealerPrice < product.price
    ? Math.round((1 - product.dealerPrice / product.price) * 100)
    : discountPercentage;

  const chips: { key: string; node: ReactNode }[] = [];

  if (discount > 0) {
    chips.push({
      key: "sale",
      node: (
        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold shadow-sm" style={{ backgroundColor: "var(--sf-badge-sale-bg)", color: "var(--sf-badge-sale-text)" }}>
          -%{discount}
        </span>
      ),
    });
  } else if (showNewBadge) {
    chips.push({
      key: "new",
      node: (
        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm" style={{ backgroundColor: "var(--sf-badge-new-bg)", color: "var(--sf-badge-new-text)" }}>
          New
        </span>
      ),
    });
  }

  if (isLowStock) {
    chips.push({
      key: "low",
      node: (
        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shadow-sm" style={{ backgroundColor: "var(--sf-badge-low-stock-bg)", color: "var(--sf-badge-low-stock-text)" }}>
          Son {product.stock} adet
        </span>
      ),
    });
  }

  const handleAddToCart = (btnElement: HTMLButtonElement) => {
    setFlyStartRect(btnElement.getBoundingClientRect());
    setIsFlying(true);
  };

  return (
    <>
      <FlyToCart
        isFlying={isFlying}
        startRect={flyStartRect}
        productImageUrl={mediaUrl(product.coverImageUrl)}
        onDone={() => setIsFlying(false)}
      />
      <Link href={`/products/${product.slug}`} className="group block h-full">
        <div
          className="flex h-full flex-col overflow-hidden rounded-[calc(var(--sf-radius)*1.25)] border transition-all duration-300 hover:-translate-y-1"
          style={{ backgroundColor: "var(--sf-card-bg)", borderColor: "var(--sf-card-border)" }}
        >
          <div className="relative aspect-[4/5] overflow-hidden bg-[var(--sf-accent-light)]">
            <SafeImage
              src={product.coverImageUrl ? mediaUrl(product.coverImageUrl) : undefined}
              alt={product.name}
              fill
              priority={index < 3}
              placeholder="blur"
              blurDataURL={BLUR_DATA_URL}
              className="object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              style={{ objectPosition: `${product.coverFocalX ?? 50}% ${product.coverFocalY ?? 50}%` }}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
              fallbackClassName="text-gray-200"
            />

            {isOutOfStock && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/55">
                <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold uppercase tracking-widest text-gray-900">
                  Tükendi
                </span>
              </div>
            )}

            <div className="absolute right-2 top-2 z-10 flex items-center gap-1 transition-all duration-200 ease-out sm:translate-y-1.5 sm:opacity-0 sm:group-hover:translate-y-0 sm:group-hover:opacity-100 sm:group-focus-within:translate-y-0 sm:group-focus-within:opacity-100">
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setQuickViewOpen(true);
                }}
                aria-label="Hızlı görünüm"
                className="hidden h-8 w-8 items-center justify-center rounded-full border border-[var(--sf-card-border)] bg-white/90 text-gray-500 shadow-sm backdrop-blur-sm transition-all hover:scale-110 hover:text-gray-800 sm:flex"
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
              <CompareButton product={product} size="sm" className="hidden h-8 w-8 sm:flex" />
              <WishlistButton
                productId={product.id}
                name={product.name}
                slug={product.slug}
                price={product.price}
                coverImageUrl={product.coverImageUrl}
                size="sm"
                className="h-8 w-8"
              />
            </div>

            {chips.length > 0 && (
              <div className="pointer-events-none absolute bottom-11 left-2 z-10">
                <ChipTicker chips={chips} />
              </div>
            )}

            <QuickAddButton product={product} onAdd={handleAddToCart} />
          </div>

          <div className="flex flex-1 flex-col p-4 text-left">
            {product.brandName && (
              <p className="mb-1.5 w-full truncate text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--sf-secondary)]">
                {product.brandName}
              </p>
            )}
            <h3
              className="mb-3 min-h-[2.75rem] text-base leading-snug line-clamp-2"
              style={{ color: "var(--sf-card-title)", fontFamily: "var(--sf-font-heading)" }}
            >
              {product.name}
            </h3>
            <div className="mt-auto flex flex-col gap-1">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                {dealerPrice ? (
                  <>
                    <span className="whitespace-nowrap text-xs text-gray-400 line-through">
                      {formatCurrency(product.price)}
                    </span>
                    <span className="whitespace-nowrap text-sm font-bold" style={{ color: "var(--sf-card-price)" }}>
                      {formatCurrency(dealerPrice)}
                    </span>
                  </>
                ) : (
                  <span className={`whitespace-nowrap text-sm font-bold ${isOutOfStock ? "text-gray-400" : ""}`} style={isOutOfStock ? undefined : { color: "var(--sf-card-price)" }}>
                    {formatCurrency(product.price)}
                  </span>
                )}
                {product.compareAtPrice && product.compareAtPrice > product.price && !dealerPrice && (
                  <span className="whitespace-nowrap text-xs text-gray-400 line-through">
                    {formatCurrency(product.compareAtPrice)}
                  </span>
                )}
              </div>
              {dealerPrice && displayDiscountPercentage > 0 && (
                <span className="text-[10px] font-bold text-emerald-700">
                  %{displayDiscountPercentage} İndirimli
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>

      {quickViewOpen && (
        <QuickViewModal slug={product.slug} onClose={() => setQuickViewOpen(false)} />
      )}
    </>
  );
}
